<script lang="ts">
import {Component, Vue} from "vue-facing-decorator";
import NavBar from "@/component/common/NavBar.vue";
import HeroSection from "@/component/HeroSection.vue";
import InfraLayerCard from "@/component/project/InfraLayerCard.vue";
import { usePostHog } from '@/composables/usePostHog';
import { useRouter } from 'vue-router';

@Component({
  components: {
    HeroSection,
    NavBar,
    InfraLayerCard
  },
})
export default class HomeView extends Vue {
  private posthog = usePostHog().posthog;
  private router = useRouter();

  // Scroll tracking properties
  private trackedDepths = new Set<number>();
  private depthMilestones = [25, 50, 75, 90, 100];

  public navigateToProjects() {
    this.posthog.capture('home_cta_projects_clicked');
    this.router.push({ name: 'projects' });
  }

  public navigateToInfraLayer(layer: string) {
    this.posthog.capture('home_infra_card_clicked', { layer: layer });
    this.router.push({ name: 'infrastructure' });
  }

  private handleScroll = () => {
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollHeight <= 0) return;

    const scrollPosition = window.scrollY;
    const scrollPercentage = Math.round((scrollPosition / scrollHeight) * 100);

    this.depthMilestones.forEach(depth => {
      if (scrollPercentage >= depth && !this.trackedDepths.has(depth)) {
        this.trackedDepths.add(depth);
        this.posthog.capture('page_scrolled', {
          page_name: 'home',
          depth_percentage: depth
        });
      }
    });
  };

  mounted() {
    window.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  unmounted() {
    window.removeEventListener('scroll', this.handleScroll);
  }
}
</script>

<template>
  <NavBar/>
  <main class="home-main">
    <!-- Hero Section -->
    <hero-section />
    
    <!-- Cloud Platform Section -->
    <div class="infra-section-anchor"></div>
    <section class="infra-overview-section">
      <div class="section-header">
        <h2 class="section-title">{{ $t('infra.title') }}</h2>
        <p class="section-subtitle">{{ $t('infra.intro') }}</p>
      </div>

      <div class="layers-horizontal-container">
        <!-- Layer 0 Card -->
        <InfraLayerCard 
          :layerNumber="$t('infra.layers.l0.name')"
          :title="$t('infra.layers.l0.title')"
          :description="$t('infra.layers.l0.shortDesc')"
          badge="OSI 1-2"
          icon="mdi-server-network"
          variant="ln-glass-teal"
          @click="navigateToInfraLayer('l0')"
        />

        <!-- Layer 1 Card -->
        <InfraLayerCard 
          :layerNumber="$t('infra.layers.l1.name')"
          :title="$t('infra.layers.l1.title')"
          :description="$t('infra.layers.l1.shortDesc')"
          badge="OSI 3-4"
          icon="mdi-kubernetes"
          variant="ln-glass-terracotta"
          @click="navigateToInfraLayer('l1')"
        />

        <!-- Layer 2 Card -->
        <InfraLayerCard 
          :layerNumber="$t('infra.layers.l2.name')"
          :title="$t('infra.layers.l2.title')"
          :description="$t('infra.layers.l2.shortDesc')"
          badge="OSI 5-6"
          icon="mdi-database"
          variant="ln-glass-stone"
          @click="navigateToInfraLayer('l2')"
        />

        <!-- Layer 3 Card -->
        <InfraLayerCard 
          :layerNumber="$t('infra.layers.l3.name')"
          :title="$t('infra.layers.l3.title')"
          :description="$t('infra.layers.l3.shortDesc')"
          badge="OSI 7"
          icon="mdi-shield-lock"
          variant="ln-glass-elevated"
          @click="navigateToInfraLayer('l3')"
        />
      </div>
    </section>

    <!-- Project CTA Section -->
    <section class="project-cta-section">
      <div class="cta-card ln-glass-elevated" @click="navigateToProjects">
        <div class="cta-visual-bg">
          <span class="mdi mdi-rocket-launch-outline"></span>
        </div>
        <div class="cta-content">
          <span class="cta-badge">{{ $t('nav.projects') }}</span>
          <h2 class="cta-title">{{ $t('home.cta.title') }}</h2>
          <p class="cta-description">{{ $t('home.cta.description') }}</p>
          <button class="cta-button">
            <span>{{ $t('home.cta.button') }}</span>
            <span class="mdi mdi-arrow-right"></span>
          </button>
        </div>
      </div>
    </section>
  </main>
</template>

<style scoped>
.home-main {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: transparent;
  padding-bottom: 10rem;
}

/* === INFRA OVERVIEW SECTION === */
.infra-overview-section {
  padding: 6rem;
  display: flex;
  flex-direction: column;
  gap: 4rem;
  max-width: var(--ln-content-max-width);
  margin: 0 auto;
}

.section-header {
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
}

.section-title {
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, var(--ln-text-primary) 30%, var(--ln-teal-deep));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.section-subtitle {
  font-size: 1.25rem;
  color: var(--ln-text-secondary);
  line-height: 1.6;
}

/* === LAYERS HORIZONTAL (No Overflow) === */
.layers-horizontal-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  width: 100%;
}

.layer-step-card {
  display: flex;
  flex-direction: column;
  padding: 3rem 2rem;
  border-radius: var(--ln-radius-lg);
  cursor: pointer;
  transition: all var(--ln-transition-normal) var(--ln-ease-bounce);
  background: var(--ln-glass-base);
  border: 1px solid var(--ln-glass-border);
  backdrop-filter: blur(var(--ln-glass-blur));
  position: relative;
  min-height: 500px;
}

.layer-step-card:hover {
  transform: translateY(-12px);
  box-shadow: 0 20px 40px var(--ln-glass-shadow-elevated);
  border-color: var(--ln-teal-deep);
}

.icon-circle {
  width: 60px;
  height: 60px;
  background: var(--ln-glass-surface);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  border: 1px solid var(--ln-glass-border);
}

.icon-circle span.mdi {
  font-size: 2rem;
  color: var(--ln-teal-deep);
}

.layer-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 0.3rem 0.7rem;
  background: var(--ln-glass-surface);
  border-radius: 2rem;
  color: var(--ln-teal-deep);
}

.layer-number {
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 1.5px;
  color: var(--ln-stone-grey);
  margin-bottom: 0.5rem;
}

.layer-step-card h3 {
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--ln-text-primary);
  line-height: 1.2;
  margin-bottom: 1.2rem;
}

.layer-step-card p {
  font-size: 1.1rem;
  color: var(--ln-text-muted);
  line-height: 1.6;
}

.explore-link {
  margin-top: auto; /* Force le bouton tout en bas */
  padding: 0.8rem 0;
  background: transparent;
  color: var(--ln-teal-deep);
  border-top: 1px solid var(--ln-glass-border);
  font-weight: 800;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
}

.layer-step-card:hover .explore-link {
  color: var(--ln-teal-light);
  border-top-color: var(--ln-teal-deep);
  padding-left: 0.5rem;
}

.layer-step-card:hover .mdi-arrow-right {
  transform: translateX(8px);
}

@media (max-width: 1200px) {
  .layers-horizontal-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
}

@media (max-width: 600px) {
  .layers-horizontal-container {
    grid-template-columns: 1fr;
  }
}

/* === PROJECT CTA SECTION === */
.project-cta-section {
  padding: 4rem 6rem;
  max-width: var(--ln-content-max-width);
  margin: 0 auto;
  width: 100%;
}

.cta-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 5rem 2rem;
  border-radius: var(--ln-radius-lg);
  cursor: pointer;
  transition: all var(--ln-transition-normal) var(--ln-ease-bounce);
  border: 1px solid var(--ln-glass-border-strong);
  overflow: hidden;
  position: relative;
  background: var(--ln-glass-base);
}

.cta-card:hover {
  transform: translateY(-8px);
  border-color: var(--ln-teal-deep);
  box-shadow: 0 30px 60px var(--ln-glass-shadow-elevated);
}

.cta-content {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 900px; /* Plus large comme demandé */
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cta-badge {
  display: inline-block;
  padding: 0.6rem 1.2rem;
  background: var(--ln-glass-surface);
  border: 1px solid var(--ln-glass-border);
  color: var(--ln-teal-deep);
  border-radius: 2rem;
  font-size: 0.85rem;
  font-weight: 800;
  margin-bottom: 2rem;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.cta-title {
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, var(--ln-text-primary) 30%, var(--ln-teal-deep));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;
}

.cta-description {
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  color: var(--ln-text-secondary);
  margin-bottom: 3rem;
  line-height: 1.6;
  max-width: 800px;
}

.cta-button {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.2rem 3rem;
  background: var(--ln-teal-deep);
  color: white;
  border: none;
  border-radius: var(--ln-radius-md);
  font-weight: 800;
  font-size: 1.2rem;
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
  box-shadow: 0 10px 20px rgba(44, 122, 123, 0.2);
}

.cta-card:hover .cta-button {
  background: var(--ln-teal-light);
  transform: scale(1.05);
  box-shadow: 0 15px 30px rgba(44, 122, 123, 0.3);
}

.cta-visual-bg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 25rem;
  color: var(--ln-teal-deep);
  opacity: 0.03;
  pointer-events: none;
  z-index: 1;
  transition: all 0.8s var(--ln-ease-smooth);
}

.cta-card:hover .cta-visual-bg {
  opacity: 0.06;
  transform: translate(-50%, -50%) rotate(5deg) scale(1.1);
}

/* === ANCHORS === */
.infra-section-anchor {
  height: 0;
  position: relative;
  top: -40px;
}

/* === RESPONSIVE === */
@media (max-width: 1024px) {
  .infra-overview-section, .project-cta-section {
    padding: 4rem 2rem;
  }
}

@media (max-width: 768px) {
  .infra-overview-section {
    padding: 3rem 1.5rem;
    gap: 3rem;
  }
  .cta-card {
    padding: 4rem 1.5rem;
  }
  .cta-visual-bg {
    font-size: 15rem;
  }
}
</style>
